import { Recipe, RecipeStep } from '../models/Recipe';
import {
  IngredientSubstitutionData,
  SubstitutionResponse
} from '../services/ingredientSubstitutionService';

// Recipe type guards
export function isValidRecipe(data: unknown): data is Recipe {
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  const recipe = data as Record<string, unknown>;
  
  return (
    typeof recipe.id === 'string' &&
    typeof recipe.title === 'string' &&
    typeof recipe.ingredients === 'string' &&
    typeof recipe.notes === 'string' &&
    (recipe.steps === undefined || isValidRecipeSteps(recipe.steps)) &&
    (recipe.image === undefined || typeof recipe.image === 'string')
  );
}

export function isValidRecipeStep(data: unknown): data is RecipeStep {
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  const step = data as Record<string, unknown>;
  
  return (
    typeof step.id === 'string' &&
    typeof step.content === 'string' &&
    (step.image === undefined || typeof step.image === 'string') &&
    (step.audio === undefined || typeof step.audio === 'string')
  );
}

export function isValidRecipeSteps(data: unknown): data is RecipeStep[] {
  if (!Array.isArray(data)) {
    return false;
  }
  return data.every(isValidRecipeStep);
}

// Ingredient substitution type guards
export function isValidIngredientSubstitution(data: unknown): data is IngredientSubstitutionData {
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  const substitution = data as Record<string, unknown>;
  const validConfidences = ['high', 'medium', 'low'];
  
  return (
    typeof substitution.original === 'string' &&
    typeof substitution.substitute === 'string' &&
    typeof substitution.ratio === 'string' &&
    typeof substitution.notes === 'string' &&
    (substitution.confidence === undefined || 
     (typeof substitution.confidence === 'string' && 
      validConfidences.includes(substitution.confidence)))
  );
}

export function isValidSubstitutionResponse(data: unknown): data is SubstitutionResponse {
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  const response = data as Record<string, unknown>;
  
  return (
    Array.isArray(response.substitutions) &&
    response.substitutions.every(isValidIngredientSubstitution) &&
    (response.generalAdvice === undefined || typeof response.generalAdvice === 'string')
  );
}

// OpenAI response validation helpers
export function validateAndParseJSON<T>(
  jsonString: string, 
  validator: (data: unknown) => data is T,
  fallback: T
): T {
  try {
    // Try to extract JSON from the response
    const jsonMatch = jsonString.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.warn('No JSON found in response, using fallback');
      return fallback;
    }
    
    const parsed = JSON.parse(jsonMatch[0]);
    
    if (validator(parsed)) {
      return parsed;
    } else {
      console.warn('Parsed JSON failed validation, using fallback');
      return fallback;
    }
  } catch (error) {
    console.error('Error parsing JSON response:', error);
    return fallback;
  }
}

// Sanitize user input to prevent injection
export function sanitizeInput(input: string): string {
  // Remove any potential script tags or dangerous content
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]+>/g, '') // Remove all HTML tags
    .trim();
}

// Validate API key format
export function isValidApiKey(key: string): boolean {
  // OpenAI API keys start with 'sk-' and are typically 51 characters
  return /^sk-[A-Za-z0-9]{48}$/.test(key);
}